import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Menu, X } from "lucide-react";
import { waLink, BRAND, LOGO_ICON, INSTAGRAM_URL } from "@/lib/contact";

const links = [
  { label: "خدماتنا", href: "#services" },
  { label: "أعمالنا", href: "#portfolio" },
  { label: "كيف نبدأ", href: "#how" },
  { label: "تواصل", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${scrolled || open ? "glass py-3 border-b border-white/10" : "py-5"}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#top" onClick={(e) => { e.preventDefault(); setOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-2.5" data-testid="navbar-logo">
          <img src={LOGO_ICON} alt="زخرف" className="w-10 h-10 rounded-lg object-cover ring-1 ring-gold/50" />
          <span className="font-serif text-3xl font-bold text-white leading-none">{BRAND}</span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button key={l.href} onClick={() => go(l.href)} className="text-sm text-white/85 hover:text-gold transition-colors duration-300" data-testid={`nav-${l.href.replace("#", "")}`}>
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="hidden sm:flex text-white/85 hover:text-gold transition-colors" data-testid="navbar-instagram" aria-label="انستغرام">
            <Instagram className="w-5 h-5" />
          </a>
          <a href={waLink("مرحبًا زخرف، أرغب بطلب تصميم دعوة إلكترونية.")} target="_blank" rel="noopener noreferrer" className="hidden sm:block rounded-full bg-white px-5 py-2.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.04]" style={{ color: "#3a4a78" }} data-testid="navbar-order-btn">
            اطلب تصميمك
          </a>
          <button onClick={() => setOpen((o) => !o)} className="md:hidden text-white p-1" data-testid="navbar-mobile-toggle" aria-label="القائمة">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden"
            data-testid="navbar-mobile-menu"
          >
            <div className="px-6 pt-4 pb-6 flex flex-col gap-1">
              {links.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="text-right py-3 text-white/90 border-b border-white/10 hover:text-gold transition-colors" data-testid={`mobile-nav-${l.href.replace("#", "")}`}>
                  {l.label}
                </button>
              ))}
              <div className="flex items-center gap-3 mt-4">
                <a href={waLink("مرحبًا زخرف، أرغب بطلب تصميم دعوة إلكترونية.")} target="_blank" rel="noopener noreferrer" className="flex-1 text-center rounded-full bg-white px-5 py-3 text-sm font-semibold" style={{ color: "#3a4a78" }} data-testid="mobile-order-btn">
                  اطلب تصميمك
                </a>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full flex items-center justify-center text-white" style={{ background: "linear-gradient(45deg,#f09433,#dc2743,#bc1888)" }} data-testid="mobile-instagram" aria-label="انستغرام">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
